import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { isoServices, certificationSteps } from '../mockData';
import { getIcon } from '../utils/icons';
import { ArrowRight, CheckCircle, Shield, Award, DollarSign, Globe, GraduationCap, Target } from 'lucide-react';

const Homepage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-[600px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtYW5hZ2VtZW50JTIwdGVhbXxlbnwwfHx8fDE3NjkwMzI4NTJ8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#003f6d]/95 via-[#003f6d]/85 to-[#003f6d]/75"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-fade-in">
            Elevate Your Business with
            <span className="block mt-2 bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">
              International Certification
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Trusted ISO certification partner for organizations worldwide. Achieve excellence in quality, security, and service management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/certification">
              <Button className="bg-[#d4af37] hover:bg-[#c49b2a] text-[#003f6d] font-semibold px-8 py-6 text-lg">
                Explore Our Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#003f6d] px-8 py-6 text-lg"
              >
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ISO Services Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our ISO Certification Services
            </h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive certification solutions for the most critical international standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {isoServices.map((service) => {
              const Icon = getIcon(service.icon);
              return (
                <Card
                  key={service.id}
                  className="group hover:shadow-2xl transition-all duration-300 border-t-4 hover:-translate-y-2"
                  style={{ borderTopColor: service.color }}
                >
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: `${service.color}15` }}
                      >
                        <Icon className="w-8 h-8" style={{ color: service.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{service.code}</h3>
                        <h4 className="text-lg font-semibold text-gray-700 mb-3">
                          {service.title}
                        </h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6">{service.shortDescription}</p>
                    <Link to={`/certification/${service.id}`}>
                      <Button
                        variant="outline"
                        className="w-full group-hover:bg-[#003f6d] group-hover:text-white group-hover:border-[#003f6d] transition-all duration-300"
                      >
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certification Process */}
      <section className="py-20 bg-[#003f6d] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Your Path to Certification</h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A streamlined, transparent process designed for your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certificationSteps.map((step) => {
              const Icon = getIcon(step.icon);
              return (
                <div
                  key={step.step}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-6 hover:bg-white/20 transition-all duration-300 border border-white/20"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-[#d4af37] text-[#003f6d] rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                      {step.step}
                    </div>
                    <Icon className="w-8 h-8 text-[#d4af37]" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-gray-300">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose QSC */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose QSC America?
            </h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A certification partner built on independence, expertise, and transparency
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              { icon: Shield, title: 'Independent & Impartial', description: 'A certification body free of consultancy conflicts, ensuring unbiased results.' },
              { icon: Award, title: 'Expert Auditors', description: 'Deep industry knowledge across all certified schemes.' },
              { icon: DollarSign, title: 'Transparent Pricing', description: 'No hidden costs — clear quotes from day one.' },
              { icon: Globe, title: 'Flexible Auditing', description: 'Remote, hybrid, and on-site audit options to fit your operations.' },
              { icon: GraduationCap, title: 'Ongoing Support', description: 'Comprehensive training and continued support for your team.' },
              { icon: Target, title: 'Consistent Decisions', description: 'Evidence-based assessments applied consistently across all clients and sectors.' }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card
                  key={index}
                  className="group border-t-4 border-t-[#d4af37] hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <CardContent className="p-8">
                    <div className="bg-[#003f6d]/10 group-hover:bg-[#003f6d] p-3 rounded-lg w-fit mb-4 transition-all duration-300">
                      <Icon className="w-8 h-8 text-[#003f6d] group-hover:text-white transition-all duration-300" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Link to="/about">
              <Button className="bg-[#003f6d] hover:bg-[#002a4d] text-white px-8 py-6 text-lg">
                Learn More About Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#003f6d] via-[#004a85] to-[#003f6d]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Your Certification Journey?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Get a tailored certification plan and customized quote for your organization
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#d4af37] hover:bg-[#c49b2a] text-[#003f6d] font-semibold px-8 py-6 text-lg">
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/certification">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#003f6d] px-8 py-6 text-lg"
              >
                View All Standards
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
